import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ROUTES } from "@/config/routes";
import { applyAccountIdFromSearch } from "@/lib/auth/account";
import { bootstrapAuth, hasAuthenticatedAccount } from "@/lib/auth/session";
import { hadSessionBefore } from "@/lib/auth/sessionMarker";
import {
  nextProtectedGateState,
  type ProtectedGateState,
} from "@/features/layout/protectedGate";

export function ProtectedApp() {
  const location = useLocation();
  const initialSearch = useRef(location.search);
  const [state, setState] = useState<ProtectedGateState>(() => {
    applyAccountIdFromSearch(location.search);
    return "loading";
  });

  useEffect(() => {
    let cancelled = false;
    applyAccountIdFromSearch(initialSearch.current);
    void bootstrapAuth()
      .then((result) => {
        if (cancelled) {
          return;
        }
        setState((prev) =>
          nextProtectedGateState(prev, result, hasAuthenticatedAccount()),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setState((prev) =>
            nextProtectedGateState(prev, "guest", hasAuthenticatedAccount()),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] text-sm text-[var(--muted)]">
        Loading…
      </div>
    );
  }

  if (state === "app_uninstalled") {
    return <Navigate to={ROUTES.appUninstalled} replace />;
  }

  if (state === "guest") {
    applyAccountIdFromSearch(location.search);
    if (hasAuthenticatedAccount()) {
      return <Outlet />;
    }
    const target = hadSessionBefore() ? ROUTES.sessionExpired : ROUTES.welcome;
    return <Navigate to={target} replace state={{ from: location.pathname }} />;
  }

  return <Outlet />;
}
